import React from 'react';

/**
 * FilterSelect — a modern filter dropdown that keeps the familiar rounded box,
 * shows a dimmed label + emphasized value + a custom chevron, and tints green
 * when a non-default value is active.
 *
 * Props:
 *   label        — dimmed field label shown before the value (e.g. "Status")
 *   value        — currently selected value
 *   options      — [{ value, label }]
 *   onChange     — (value) => void
 *   defaultValue — the "unfiltered" value (no green tint when selected). Default 'all'.
 *   minWidth     — optional min width in px
 */
const FilterSelect = ({ label, value, options, onChange, defaultValue = 'all', minWidth = 0 }) => {
  const [open, setOpen] = React.useState(false);
  const selected = options.find(o => o.value === value);
  const isActive = value !== defaultValue;
  return (
    <div className="relative" style={minWidth ? { minWidth } : undefined}>
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className={`h-[30px] w-full px-2.5 rounded border text-sm flex items-center gap-1.5 transition-colors ${
          isActive
            ? 'bg-emerald-50 border-emerald-300 hover:bg-emerald-100/70'
            : 'bg-white border-gray-300 hover:bg-gray-50'
        }`}
      >
        <span className={isActive ? 'text-emerald-700/70' : 'text-gray-400'}>{label}</span>
        <span className={`font-medium truncate ${isActive ? 'text-emerald-800' : 'text-gray-800'}`}>{selected?.label || ''}</span>
        <svg className={`w-3 h-3 ml-auto flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''} ${isActive ? 'text-emerald-600' : 'text-gray-400'}`}
          fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute left-0 mt-1 min-w-full w-max max-w-[240px] max-h-72 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-1">
            {options.map(o => (
              <button
                key={o.value}
                onClick={() => { onChange(o.value); setOpen(false); }}
                className={`w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 transition-colors hover:bg-emerald-50 ${
                  o.value === value ? 'text-emerald-700 font-medium' : 'text-gray-700'
                }`}
              >
                <span className="w-3.5 flex-shrink-0">
                  {o.value === value && (
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                  )}
                </span>
                <span className="truncate">{o.label}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default FilterSelect;
